'use client';

import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { useAnalytics } from '@modules/shared/analytics/hooks/useAnalytics';
import { CustomSpin } from '@modules/shared/components/CustomSpin';
import useFetcher from 'app/_utils/data/useFetcher';
import { useGetMemberships } from 'app/_utils/data/useGetMemberships';

import * as Sentry from '@sentry/nextjs';
import { getCookie, setCookie } from 'cookies-next';
import lodashMerge from 'lodash/merge';
import React, { createContext } from 'react';
import { UserInfo } from 'shared/src/user/types';
import useSWR from 'swr';

const USER_PREFERENCES_COOKIE_KEY = 'userPreferences';

export type UserContextData = UserInfo & {
  memberships: ReturnType<typeof useGetMemberships>;
  mutateUser: () => void;
  loading: boolean;
  userPreferences: UserPreferences;
  updateUserPreferences: (userPreferences: Partial<UserPreferences>) => void;
};

export const UserContext = createContext<UserContextData>({} as UserContextData);

type UserPreferences = {
  [organizationId: string]: {
    currentWorkspaceId: string;
  };
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<UserInfo>({} as UserInfo);
  const { user: authUser, isLoading: authInfoLoading } = useCommonAuthProvider();
  const analytics = useAnalytics();
  const [userInfoLoaded, setUserInfoLoaded] = React.useState<boolean>(false);
  const { authorizedOrganizationId } = useAuthProvider();
  const { cloudMspEnableUiFeatures } = useFlagsProxy();
  const memberships = useGetMemberships(cloudMspEnableUiFeatures);
  const { fetcher } = useFetcher();
  const {
    mutate: mutateUser,
    data: userInfo,
    isLoading: isUserInfoLoading,
  } = useSWR<unknown, unknown>(
    authorizedOrganizationId ? `${process.env.ZEUS_API_URL}/v1/tenants/${authorizedOrganizationId}/userInfo` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );
  const [userPreferences, setUserPreferences] = React.useState<UserPreferences>({});

  const updateUserPreferences = React.useCallback((preferences: Partial<UserPreferences>): void => {
    setUserPreferences((prev) => {
      // userPreferences is a partial object, so we need to deep merge it with prev
      const updatedUserPreferences = lodashMerge({}, prev, preferences);

      const MONTH_IN_MILLISECONDS = 2592000000;

      setCookie(USER_PREFERENCES_COOKIE_KEY, JSON.stringify(updatedUserPreferences), {
        domain: `.${process.env.SHARED_DOMAIN}`,
        expires: new Date(Date.now() + MONTH_IN_MILLISECONDS),
        sameSite: 'none',
        secure: true,
      });

      return updatedUserPreferences;
    });
  }, []);

  React.useEffect(() => {
    updateUserPreferences(JSON.parse(getCookie(USER_PREFERENCES_COOKIE_KEY) || '{}'));
  }, [updateUserPreferences]);

  React.useEffect(() => {
    if (authInfoLoading || isUserInfoLoading) {
      return;
    }

    analytics.identify({ userId: authUser?.sub });
  }, [authUser?.sub, authInfoLoading, isUserInfoLoading]);

  React.useEffect(() => {
    if (authInfoLoading || isUserInfoLoading) {
      return;
    }

    if (authUser) {
      Sentry.setUser({ id: authUser.sub });
    }

    setUser((prev) => ({
      ...prev,
      ...(userInfo || {}),
      ...(authUser || {}),
      isOEM: !!(authUser?.['https://cribl.cloud/branding'] && authUser['https://cribl.cloud/branding'] !== 'Cribl'),
    }));
    setUserInfoLoaded(true);
  }, [authUser, userInfo, isUserInfoLoading, authInfoLoading, authorizedOrganizationId]);

  const value = React.useMemo(
    () => ({
      ...user,
      mutateUser,
      loading: !userInfoLoaded || authInfoLoading,
      userPreferences,
      updateUserPreferences,
      memberships,
    }),
    [user, mutateUser, userInfoLoaded, authInfoLoading, userPreferences, updateUserPreferences, memberships.data],
  );

  if (!memberships.data || !userInfoLoaded) {
    return <CustomSpin />;
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUser(): UserContextData {
  const context = React.useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
}
